import { BehaviorSubject, Observable, of, zip as observableZip } from 'rxjs';
import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { Actions, Effect } from '@ngrx/effects'
import { Action, select, Store } from '@ngrx/store'
import { catchError, filter, map, switchMap, take, withLatestFrom } from 'rxjs/operators'

import * as user from '../../auth/actions/user'
import * as catalog from '../actions/catalog'
import * as facet from '../actions/facet'
import * as pages from '../../base/actions/pages.actions'
import * as status from '../../base/actions/status.actions'
import * as product from '../actions/product'

import * as fromCatalog from '../reducers'
import * as fromBase from '../../base/reducers'

import { ThemeConfig } from '../../core/helpers/theme-config'

const catalogRoot = ThemeConfig.get<string>('catalog', 'route-name')

@Injectable()
export class CatalogEffects {
    pagesReady = new BehaviorSubject<boolean>(false)

    constructor(
        private actions$: Actions,
        private store: Store<fromCatalog.State>,
        private router: Router
    ) {
        store.pipe(select(fromBase.isPagesReady))
            .subscribe(this.pagesReady)
    }

    @Effect()
    getOnLangChange$: Observable<Action> =
        this.actions$.ofType<user.SetLanguage>(user.SET_LANGUAGE)
            .pipe(
                switchMap(() =>
                    this.actions$
                        .ofType<user.SetSuccess>(user.SET_SUCCESS)
                        .pipe(
                          take(1)
                        )
                ),
                withLatestFrom(
                  this.store.pipe(select(fromBase.getCurrentPageType)),
                  (action, pageType) => pageType
                ),
                filter(pageType => pageType && (pageType.toLowerCase() === 'catalog' || pageType.toLowerCase() === 'search')),
                switchMap(() => of(new catalog.Init(true), new status.Reapply()))
            )

    @Effect()
    metaInitShouldRun$: Observable<Action> =
        this.actions$.ofType<catalog.Init>(catalog.INIT)
          .pipe(
            map(action => action.forceRefresh),
            switchMap((forceRefresh) => of(new facet.InitGroups(forceRefresh), new facet.InitFacets(forceRefresh), new product.SetProductDefaultSortOrder() ))
          )

    @Effect()
    metaInitComplete$: Observable<Action> =
        observableZip(
            this.actions$.ofType<Action>(facet.GET_GROUPS_SUCCES, facet.GET_GROUPS_SKIPPED, facet.GET_GROUPS_FAILED),
            this.actions$.ofType<Action>(facet.GET_FACETS_SUCCESS, facet.GET_FACETS_SKIPPED, facet.GET_FACETS_FAILED)
        )
        .pipe(
            switchMap(actions => {
                if(this.pagesReady.getValue())
                    return of(actions)
                return this.actions$.ofType<Action>(pages.GET_SUCCESS, pages.GET_FAILED)
                    .pipe(
                        map(action => [...actions, action]),
                        take(1)
                    )
            }),
            map((actions: Action[]) => {
                let failed = actions.some(
                    action => action.type === pages.GET_FAILED
                    || action.type === facet.GET_GROUPS_FAILED
                    || action.type === facet.GET_FACETS_FAILED
                )

                if(failed)
                    return new catalog.InitFailed()
                return new catalog.InitSuccess()
            }),
            catchError(() => of(new catalog.InitFailed()))
        )
}
